import { forwardRef, type InputHTMLAttributes, type ReactNode, useState } from 'react';

import classNames from 'classnames';

import { ErrorMessage } from '../ErrorMessage/ErrorMessage';

import styles from './FormInput.module.scss';
import type { ErrorMessageMap } from './types';
import { useBuiltInValidation } from './useBuiltInValidation';

export type FormInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  errorMessageMap?: ErrorMessageMap;
  error?: string;
  noSpaceForError?: boolean;
  startAdornment?: ReactNode;
  dataTestId?: string;
  size?: 'sm' | 'md';
  hideErrors?: boolean;
};

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      errorMessageMap,
      error,
      onBlur,
      onInvalid,
      noSpaceForError,
      startAdornment,
      className,
      dataTestId,
      hideErrors,
      ...props
    },
    ref,
  ) => {
    const [localError, setLocalError] = useState<string | undefined>(undefined);

    const { handleBlur, handleInvalid } = useBuiltInValidation(setLocalError, errorMessageMap, {
      onBlur,
      onInvalid,
    });

    const { size, ...propsWithoutSize } = props;

    const inputNode = (
      <input
        ref={ref}
        className={classNames(styles.input, className, {
          [styles.inputWithPrefix]: !!startAdornment,
          [styles.sm]: size === 'sm',
        })}
        data-testid={dataTestId}
        onBlur={handleBlur}
        onInvalid={handleInvalid}
        {...propsWithoutSize}
      />
    );

    return (
      <div className={classNames(styles.formInputWrapper, { [styles.sm]: size === 'sm' })}>
        {startAdornment ? (
          <div className={classNames(styles.inputWrapper, { [styles.sm]: size === 'sm' })}>
            {startAdornment}
            {inputNode}
          </div>
        ) : (
          inputNode
        )}
        <ErrorMessage
          className={classNames(styles.errorMsg, { [styles.sm]: size === 'sm' })}
          noSpaceWhenEmpty={noSpaceForError}
        >
          {hideErrors ? null : error || localError}
        </ErrorMessage>
      </div>
    );
  },
);

FormInput.displayName = 'FormInput';
